"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { usePathname, useRouter } from "next/navigation";
import { miniChatDesktopSuppressed } from "@/lib/chat/mini-chat-routes";
import { useLocale, useTranslations } from "next-intl";
import { X } from "@phosphor-icons/react/dist/ssr";
import SparklesIcon from "@/components/ui/SparklesIcon";
import AnimatedPlaceholder from "@/components/ui/AnimatedPlaceholder";
import LiquidOrb from "./LiquidOrb";
import MiniBarShell, { ANIM_MS, useHeroRevealGate } from "./MiniBarShell";
import { localeHref } from "@/lib/locale-href";
import { writeDraft } from "@/lib/chat/contact-prefill";
import { useAutogrowTextarea } from "@/lib/hooks/use-autogrow-textarea";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";
import { useAppFrameMounted } from "@/lib/app-frame-store";
import { CHAT_EVENTS, track } from "@/lib/analytics";

/** Stable no-op subscribe: the "are we on the client yet" store never emits. */
const NEVER_CHANGES = () => () => {};

/** The field's heights, in px. Collapsed it is exactly one line (the pill is a
 *  30px control and the line box is 19.25px — see the textarea's own note).
 *  Open it starts at the three lines the panel was built around and grows with
 *  the text to `MAX`, five lines, after which it scrolls (owner, 2026-09-18).
 *  ⚠️ `leading-snug` on `text-sm` is 19.25px, so five lines is 96.25: the extra
 *  pixel is rounding, NOT slack, and a whole line will not fit in it. */
const FIELD_COLLAPSED = 20;
const FIELD_OPEN_MIN = 62;
const FIELD_OPEN_MAX = 97;

/**
 * The docked mini assistant — a resting input pill above the bottom edge that
 * grows UPWARD into a panel and hands the typed sentence to `/chat`.
 * Spec: `mini-chat-spec.md`. Approved visual: `design/mini-chat-mockup.html`.
 *
 * ⭐ IT GROWS UPWARD, AND THAT IS THE WHOLE LAYOUT ARGUMENT. The input row is
 * the fixed point: it must not slide out from under the reader's thumb as the
 * panel opens. Everything new appears ABOVE it, which is why the container is
 * pinned to the bottom and the growing section sits before the row in the DOM.
 *
 * ⭐ The pill itself — position, glass, slide, radius — is `MiniBarShell`,
 * shared with the search fallback (`mini-search-fallback-spec.md`). Only the
 * contents and the submit behaviour are this component's.
 */
export default function MiniChatBar() {
  const t = useTranslations("chat.mini");
  const locale = useLocale();
  const router = useRouter();
  const reducedMotion = useReducedMotion();
  // `/chat` is an app frame with its own pinned composer — the frame declares
  // itself, so this needs no route test (which would go stale on client-side
  // navigation, the root layout never being remounted).
  const appFrame = useAppFrameMounted();

  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  // SSR renders the plain placeholder; the typewriter starts only once mounted,
  // so there is no hydration flash and nothing nondeterministic in render.
  // `useSyncExternalStore` rather than setState-in-an-effect: it returns the
  // server snapshot (false) during SSR and the client one (true) from the very
  // first client render, with no extra commit and no cascading render.
  const mounted = useSyncExternalStore(NEVER_CHANGES, () => true, () => false);
  const [sending, setSending] = useState(false);
  const fromPrompt = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLTextAreaElement>(null);

  // Open, the field grows with the text to `FIELD_OPEN_MAX`, then scrolls
  // (shared with the hero). Collapsed it is pinned to one line: the resting
  // pill's own height is the contract there.
  useAutogrowTextarea(triggerRef, {
    value,
    min: FIELD_OPEN_MIN,
    max: FIELD_OPEN_MAX,
    fixed: expanded ? null : FIELD_COLLAPSED,
  });

  const prompts = [t("prompt1"), t("prompt2"), t("prompt3")];
  // Idle = hydrated, collapsed, unfocused and empty.
  // ⭐ Back to resting when the visitor moves on (owner, 2026-09-06). The
  // marketing layout is NEVER remounted across client navigation, so without
  // this the bar carries whatever was typed -- or the prompt that was clicked
  // on the way to /chat -- onto the next page, expanded and pre-filled.
  //
  // Adjusted during render rather than in an effect: this is React's own
  // "reset state when a value changes" pattern, it repaints in the same commit
  // (an effect would flash the stale text for a frame), and it keeps the
  // component clear of `react-hooks/set-state-in-effect`.
  const pathname = usePathname();
  // Mobile-only on the two surfaces that carry their own persistent desktop
  // affordance. Pure + tested — see the module.
  const desktopSuppressed = miniChatDesktopSuppressed(pathname);
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setExpanded(false);
    setValue("");
    setSending(false);
    setFocused(false);
  }

  // ⭐ HOMEPAGE ONLY (owner, 2026-09-06): hold the bar back until the reader
  // has scrolled a little AND the hero input has left the viewport, so the page
  // never offers two competing entry points at once. Every other page has no
  // hero to compete with, so the bar is there from the top.
  //
  // The `[data-hero-search]` gate, shared with the search fallback — see
  // `useHeroRevealGate` for the two bugs its shape is built around.
  const revealed = useHeroRevealGate(pathname);
  // Never yank it away mid-interaction: once open it stays until it is closed.
  const onScreen = revealed || expanded;

  const showTypewriter = mounted && !expanded && !focused && value === "";

  const close = useCallback(() => {
    setExpanded(false);
    triggerRef.current?.blur();
  }, []);

  const open = useCallback(() => {
    if (expanded) return;
    setExpanded(true);
    // `window.location`, not the `pathname` above, only because this fires
    // inside a callback where either would do and this one cannot go stale.
    //
    // ⚠️ This used to carry a warning that `usePathname()` would make the
    // prerendered marketing shell dynamic on every [slug] route. MEASURED on
    // Next 16.2.1 (2026-09-06) and it does not: builds with and without the
    // hook produced the same 61 routes, the same 43 partial-prerendered, the
    // same 261 prerendered shells, and byte-identical shell HTML apart from
    // chunk hashes. The hook is used above to reset the bar between pages.
    track(CHAT_EVENTS.miniChatExpanded, { path: window.location.pathname });
  }, [expanded]);

  /**
   * `preset` lets a prompt button ask its question in the SAME tick it is
   * clicked. Routing it through `setValue` first would mean reading `value`
   * one render too early and handing over an empty string.
   * ⚠️ Never wire this straight to `onClick` — the MouseEvent would land in
   * `preset`. Call sites pass nothing, or a string.
   */
  const send = useCallback((preset?: string) => {
    const text = (preset ?? value).trim();
    setSending(true);
    // Hand the sentence over BEFORE navigating. `writeDraft` is sessionStorage
    // and `ChatClient` clears it on read, so a stale draft cannot resurface.
    // `auto` = ChatClient SENDS it on arrival rather than parking it in the
    // composer: the visitor already committed to the question here.
    // Empty is NOT a no-op: the arrow still opens /chat, as it did when it was
    // labelled "open the assistant". Returning early here would leave a dead
    // control on the resting pill.
    if (text) writeDraft(text, { auto: true });
    track(CHAT_EVENTS.miniChatSent, {
      path: window.location.pathname,
      chars: text.length, // a LENGTH, never the text itself
      fromPrompt: fromPrompt.current,
    });
    router.push(localeHref("/chat", locale));
  }, [value, locale, router]);

  // ⭐ THE OPEN PANEL IS NOT MODAL (owner, 2026-09-13): no scrim, it just
  // expands. The page behind stays lit and usable, so trapping Tab inside the
  // panel would be a lie and the old focus trap is gone. What the scrim's
  // click-to-close did is kept: a pointer press or a focus move OUTSIDE the
  // panel collapses it, and Escape still closes.
  useEffect(() => {
    if (!expanded) return;
    const outside = (target: EventTarget | null) =>
      !!panelRef.current && target instanceof Node && !panelRef.current.contains(target);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    const onPointer = (e: PointerEvent) => { if (outside(e.target)) close(); };
    const onFocusIn = (e: FocusEvent) => { if (outside(e.target)) close(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [expanded, close]);

  // Never over the assistant's own page.
  if (appFrame) return null;

  const grow = (children: React.ReactNode) => (
    // `grid-template-rows: 0fr → 1fr` animates height with no magic max-height
    // to outgrow. The row below stays put; only this section changes size.
    <div
      className="grid"
      style={{
        gridTemplateRows: expanded ? "1fr" : "0fr",
        transition: reducedMotion ? "none" : `grid-template-rows ${ANIM_MS}ms cubic-bezier(.2,0,0,1)`,
      }}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );

  return (
    <>
      <MiniBarShell
        onScreen={onScreen}
        reducedMotion={reducedMotion}
        desktopSuppressed={desktopSuppressed}
        expanded={expanded}
        panelRef={panelRef}
        role={expanded ? "dialog" : undefined}
        ariaLabel={expanded ? t("title") : undefined}
      >
        {grow(
          <>
            <div className="flex items-center gap-8 px-13 pt-13">
              <span className="font-serif text-sm font-semibold text-navy">{t("title")}</span>
              <button
                type="button"
                onClick={close}
                aria-label={t("close")}
                className="ml-auto grid h-28 w-28 place-items-center rounded-control text-gray-500 transition-colors hover:bg-navy/[0.05] hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
              >
                <X size={14} weight="bold" aria-hidden="true" />
              </button>
            </div>

            <div className="flex flex-col gap-5 p-13" role="group" aria-label={t("title")}>
              {prompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    // A prompt is a whole question, not a starting point --
                    // so it goes straight to /chat and is asked there.
                    setValue(p);
                    fromPrompt.current = true;
                    send(p);
                  }}
                  className="flex w-full items-center gap-10 rounded-control border border-navy/10 bg-surface px-13 py-11 text-left text-sm font-medium leading-tight text-navy transition-colors hover:border-navy/30 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
                >
                  <SparklesIcon size={15} />
                  {p}
                </button>
              ))}
            </div>
          </>,
        )}

        <div className={`flex gap-10 p-10 pl-13 ${expanded ? "items-end border-t border-navy/10" : "items-center"}`}>
          {/* Collapsed, the mark is the only thing saying whose input this is.
              Expanded, the panel header says it in words — so it retires and
              gives its width back to the text, which matters at 375px. */}
          <span
            aria-hidden="true"
            className="shrink-0 overflow-hidden"
            style={{
              width: expanded ? 0 : 28,
              opacity: expanded ? 0 : 1,
              marginRight: expanded ? -10 : 0,
              transition: reducedMotion ? "none" : `width ${ANIM_MS}ms cubic-bezier(.2,0,0,1), opacity 140ms ease, margin 220ms ease`,
            }}
          >
            {/* ⭐ The animated orb (owner, 2026-09-06) — a REST-state variant
                is coming later; this reuses the "thinking" one meanwhile.
                ⛔ NO BACKGROUND ON THIS SLOT. It carried `bg-navy` as a
                fallback and that IS the ring the owner saw twice: the orb's
                drawn circle is inset inside its square canvas, so a disc
                behind it shows through at the edges. ⚠️ Box measurement CANNOT
                see this — the disc and the canvas are both 28px, so the
                "ring" measured 0 while being plainly visible. Judge it by eye.
                ⚠️ Consequence, accepted: `LiquidOrb` degrades to NOTHING
                without WebGPU (Firefox, older Safari, much of Android), under
                reduced motion, or on any failure — so this slot is then EMPTY
                rather than showing a mark. The bar still reads (placeholder +
                send). The rest-state variant should carry its own static
                fallback; do not solve it by putting a disc back here. */}
            <span className="block h-28 w-28 overflow-hidden rounded-full">
              <LiquidOrb size={28} />
            </span>
          </span>

          {/* ⭐ The hero search's typewriter (owner) — the SHARED
              `AnimatedPlaceholder`, extracted rather than copied. Visible
              only while the field is genuinely idle; the native placeholder
              is blanked underneath so the two can never both paint. */}
          <span className="relative min-w-0 flex-1">
            {showTypewriter && (
              <AnimatedPlaceholder
                examples={prompts}
                reducedMotion={reducedMotion}
                className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center overflow-hidden text-sm text-gray-500"
              />
            )}
            <textarea
            ref={triggerRef}
            rows={1}
            value={value}
            onChange={(e) => { setValue(e.target.value); fromPrompt.current = false; }}
            onFocus={open}
            onClick={open}
            onKeyDown={(e) => {
              // IME-safe: never submit mid-composition (same rule as the
              // full composer on /chat).
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={showTypewriter ? "" : t("placeholder")}
            onFocusCapture={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-label={t("open")}
            // ⚠️ NO VERTICAL PADDING, and `overflow-y` hidden while collapsed.
            // `text-sm` + `leading-snug` is a 19.25px line box; with `py-8` that
            // is 35.25px inside a 30px control, so the textarea grew its own
            // scrollbar — the grey sliver visible beside the send button
            // (owner, 2026-09-06). The row centres it instead of padding doing
            // it, which also keeps the placeholder optically level with the orb.
            // ⭐ `block` is load-bearing, not decoration. A textarea is
            // inline-block by default, so the wrapper span formed a LINE BOX
            // (27.09px against a 20px control) and the field rode its
            // baseline 3.55px above centre while the orb, the send button and
            // the typewriter overlay all sat exactly on the panel's mid line.
            // Blockifying it collapses the wrapper onto the control, and the
            // row's `items-center` then centres the real thing. Measured
            // 2026-09-06: offset from panel centre 3.55px -> 0.
            className="block w-full resize-none bg-transparent text-sm leading-snug text-navy outline-none placeholder:text-gray-500"
            // The height here is only the BASELINE for the commit; the layout
            // effect above raises it to fit the text before the frame paints,
            // so an open field never renders at three lines and then jumps.
            style={{
              height: expanded ? FIELD_OPEN_MIN : FIELD_COLLAPSED,
              overflowY: expanded ? "auto" : "hidden",
              transition: reducedMotion ? "none" : `height ${ANIM_MS}ms cubic-bezier(.2,0,0,1)`,
            }}
            />
          </span>

          <button
            type="button"
            onClick={() => send()}
            disabled={sending}
            aria-label={t("send")}
            className="grid h-34 w-34 shrink-0 place-items-center rounded-full bg-gold text-navy transition-colors hover:bg-gold-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:opacity-60"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" />
            </svg>
          </button>
        </div>

        {grow(
          <p className="px-13 pb-11 text-2xs leading-snug text-gray-500">{t("disclosure")}</p>,
        )}
      </MiniBarShell>
    </>
  );
}
